import get from 'lodash/get';
import throttle from 'lodash/throttle';
import each from 'lodash/each';

import store from '@Store';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { RXTopics } from '@Constants/CommonConstants';

import SearchProgress from '@Services/SearchProgress';
import RXService from '@Services/RxService';

import {
  typeOfBatchSearchProgressModal,
  batchSearchProgressModalSetProgressStatusReducerType,
  batchSearchProgressModalSetStatusReducerType,
  batchSearchProgressModalSetQueryRunningReducerType,
  BATCH_SEARCH_PROGRESS_MODAL_STATUS,
  batchSearchProgressModalSetProgressPercentageReducerType,
  batchSearchProgressModalSetQueriesTotalReducerType,
} from './BatchSearchProgressModalConstants';
import {
  progressStatus as progressStatusSelector,
  status as statusSelector,
  queryRunning as queryRunningSelector,
  queriesTotal as queriesTotalSelector,
} from './BatchSearchProgressModalSelectors';
import { initialState } from './BatchSearchProgressModalReducer';

export const openBatchSearchProgressModal = (props) => (dispatch) => {
  dispatch(
    updateDialog({
      id: typeOfBatchSearchProgressModal,
      dialogType: typeOfBatchSearchProgressModal,
      movable: false,
      backdrop: true,
      ...props,
    })
  );
};

export const closeBatchSearchProgressModal = () => (dispatch) => {
  dispatch(
    closeDialog({
      id: typeOfBatchSearchProgressModal,
    })
  );
};

export const dispatchOpenBatchSearchProgressModal = (...args) => {
  store.dispatch(openBatchSearchProgressModal(...args));
};

export const dispatchCloseBatchSearchProgressModal = () => {
  store.dispatch(closeBatchSearchProgressModal());
};

export function setProgressStatus(progressStatus) {
  return {
    type: batchSearchProgressModalSetProgressStatusReducerType,
    payload: { progressStatus },
  };
}

export function setStatus(status) {
  return {
    type: batchSearchProgressModalSetStatusReducerType,
    payload: { status },
  };
}

export function setQueryRunning(queryRunning) {
  return {
    type: batchSearchProgressModalSetQueryRunningReducerType,
    payload: { queryRunning },
  };
}

export function setQueriesTotal(queriesTotal) {
  return {
    type: batchSearchProgressModalSetQueriesTotalReducerType,
    payload: { queriesTotal },
  };
}

export function setProgressPercentage(progressPercentage) {
  return {
    type: batchSearchProgressModalSetProgressPercentageReducerType,
    payload: { progressPercentage },
  };
}

export const resetState = () => (dispatch) => {
  dispatch(setProgressStatus(initialState.get('progressStatus')));
  dispatch(setStatus(initialState.get('status')));
  dispatch(setQueryRunning(initialState.get('queryRunning')));
  dispatch(setQueriesTotal(initialState.get('queriesTotal')));
  dispatch(setProgressPercentage(initialState.get('progressPercentage')));
};

export const cancelSearch = (searchId) => (dispatch) => {
  if (searchId) {
    SearchProgress.cancelSearch(searchId);
  }
  dispatch(closeBatchSearchProgressModal());
};

/**
 * @ngdoc function
 * @name executionStarted
 * @param {array} databasesId
 * @description check status of execution
 */
export const executionStarted = (databasesId) => (dispatch) => {
  if (databasesId && !databasesId.length) {
    dispatch(cancelSearch());
    return;
  }
  const progressStatus = {};
  each(databasesId, (databaseId) => {
    progressStatus[databaseId] = {
      queryRunning: null,
      queriesProcessed: 0,
      started: false,
      finished: false,
      skipped: false,
    };
  });

  dispatch(setProgressStatus(progressStatus));
};

export const updateQueryRunning = () => (dispatch, getState) => {
  const progressStatus = progressStatusSelector(getState());
  const queryRunning = queryRunningSelector(getState());
  const queriesTotal = queriesTotalSelector(getState());

  const update = throttle(() => {
    const progressStatusValues = Object.values(progressStatus);

    const newQrueryRunning = Math.max(...progressStatusValues.map((db) => db.queryRunning), queryRunning);
    const newProgressPercentage = Math.floor((newQrueryRunning / queriesTotal) * 100);

    dispatch(setQueryRunning(newQrueryRunning));
    dispatch(setProgressPercentage(newProgressPercentage));
  }, 1000);
  update();
};

export const singleQueryStarted = (payload) => {
  const { itemNumber, databaseId } = payload;
  return (dispatch, getState) => {
    const progressStatus = progressStatusSelector(getState());

    progressStatus[databaseId].queryRunning = itemNumber + 1;
    dispatch(updateQueryRunning());
  };
};

export const singleQueryProcessed = (databaseId) => (dispatch, getState) => {
  const progressStatus = progressStatusSelector(getState());
  const status = statusSelector(getState());
  const queryRunning = queryRunningSelector(getState());
  const queriesTotal = queriesTotalSelector(getState());

  // eslint-disable-next-line no-plusplus
  progressStatus[databaseId].queriesProcessed++;

  if (status !== BATCH_SEARCH_PROGRESS_MODAL_STATUS.COMBINING && queryRunning === queriesTotal) {
    dispatch(setStatus(BATCH_SEARCH_PROGRESS_MODAL_STATUS.COMBINING));
  }
};

export const databaseStarted = (payload) => {
  const { databaseId } = payload;
  return (dispatch, getState) => {
    const progressStatus = progressStatusSelector(getState());
    progressStatus[databaseId].started = true;
  };
};

export const databaseSkipped = (payload) => {
  const { databaseId } = payload;
  return (dispatch, getState) => {
    const progressStatus = progressStatusSelector(getState());
    progressStatus[databaseId].skipped = true;
  };
};

export const databaseProcessed = (databaseId) => (dispatch, getState) => {
  const closeCallback = () => dispatch(closeBatchSearchProgressModal());

  const progressStatus = progressStatusSelector(getState());
  progressStatus[databaseId].finished = true;
  SearchProgress.checkIfAllSearchesFinished(progressStatus, closeCallback);
};
export const destroy = () => () => {
  RXService.unsubscribe(RXTopics.batchSearch.all);
};

export const init = (batchFile) => (dispatch) => {
  dispatch(resetState());

  const queries = get(batchFile, 'queries', {});
  dispatch(setQueriesTotal(Object.keys(queries).length));

  RXService.subscribe(RXTopics.batchSearch.searchStarted, (databasesId) => dispatch(executionStarted(databasesId)));
  RXService.subscribe(RXTopics.batchSearch.singleQueryStarted, (payload) => dispatch(singleQueryStarted(payload)));
  RXService.subscribe(RXTopics.batchSearch.singleQueryProcessed, (databaseId) => dispatch(singleQueryProcessed(databaseId)));
  RXService.subscribe(RXTopics.batchSearch.databaseStarted, (payload) => dispatch(databaseStarted(payload)));
  RXService.subscribe(RXTopics.batchSearch.databaseSkipped, (payload) => dispatch(databaseSkipped(payload)));
  RXService.subscribe(RXTopics.batchSearch.databaseProcessed, (databasesId) => dispatch(databaseProcessed(databasesId)));
};
